<!-- eslint-disable indent -->
<template>
  <AppEventContainer>
    <h1 class="text-2xl font-bold">Event Summary</h1>
    <EventListItem :poker-run-event="pokerRunEvent" />

    <!-- Description -->
    <div v-if="pokerRunEvent?.description">
      {{ pokerRunEvent.description }}
    </div>

    <!-- Registration Code Input -->
    <p v-if="pokerRunEvent?.eventSettings.isPrivateEvent">This event requires a registration code.</p>
    <div v-if="pokerRunEvent?.eventSettings.isPrivateEvent">
      <AppInputText name="registrationCode" label="Registration code" v-model="enteredCode" placeholder="Enter code"
        :required="true" />
    </div>

    <!-- Register Button -->
    <AppButton v-if="canRegister" title="Register" @click="register" :disabled="isRegisterDisabled" />

    <!-- Event Stops Section -->
    <h2 class="text-lg font-semibold">Event Stops</h2>
    <AppExpandCollapse v-model="allExpanded" @update:model-value="expandCollapseAll" />

    <!-- Stops List -->
    <StopView v-for="(stop, index) in pokerRunEvent?.stops" :key="index" :show-map="true" :stop="stop"
      :expanded="expandedStops[index]" @update:expanded="(expanded) => toggleStop(index)"
      :cardReleaseType="pokerRunEvent?.eventSettings.cardReleaseType" />
  </AppEventContainer>
</template>
<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { useEventParticipantStore } from "@/store/EventParticipant";
import { useAppStore } from "@/store/App";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import useDate from "@/modules/useDate";
import { storeToRefs } from "pinia";
import StopView from '@/components/StopView.vue';
import AppExpandCollapse from '@/components/AppExpandCollapse.vue';
import EventListItem from '@/components/EventListItem.vue';
import AppInputText from '@/components/AppInputText.vue';

const { formatISODate, getDate } = useDate();
const { isOffline } = storeToRefs(useAppStore());

const route = useRoute();
const router = useRouter();
const pokerRunEvent = ref<PokerRunEvent | null>(null);
const registrationStartDate = ref();

const expandedStops = ref<boolean[]>([]);
const allExpanded = ref(false);

const today = new Date();
today.setHours(0, 0, 0, 0);

const { currentPokerRunEvent } = useEventParticipantStore();
// eslint-disable-next-line operator-linebreak
const register = () => {
  if (!pokerRunEvent.value) {
    return;
  }
  UserTransactionDataService.warmup();

  const routeConfig = {
    name: "Register",
    params: { id: pokerRunEvent.value.id },
    query: {
      ...(route.query.fundraiser ? { fundraiser: route.query.fundraiser } : {}),
      ...(requiresRegistrationCode.value ? { code: enteredCode.value } : {})
    }
  };

  router.push(routeConfig);
};

const maxCapacity = computed(() => {
  const hasCap =
    pokerRunEvent.value &&
    pokerRunEvent.value?.eventSettings.registrationCap &&
    pokerRunEvent.value?._internal?.registrationCount;

  return hasCap && pokerRunEvent.value?.eventSettings.registrationCap
    ? (pokerRunEvent.value?._internal?.registrationCount ?? 0) >=
    pokerRunEvent.value?.eventSettings.registrationCap
    : false;
});

const canRegister = computed(
  () =>
    pokerRunEvent.value?.status === "active" &&
    pokerRunEvent.value?.eventSettings.registrationStartDate <=
    formatISODate(new Date()) &&
    (pokerRunEvent.value?.eventDate as string).substring(0, 10) >= formatISODate(today) &&
    !maxCapacity.value &&
    pokerRunEvent.value?.eventSettings.registrationStatus !== "closed" &&
    !isOffline.value &&
    pokerRunEvent.value?.eventSettings?.allowInAppPurchase === true
);

const initialized = async () => {
  pokerRunEvent.value = route.params.id
    ? await PokerRunEventDataService.get(route.params.id as string)
    : currentPokerRunEvent;
  registrationStartDate.value = getDate(
    pokerRunEvent.value.eventSettings.registrationStartDate
  );

  expandedStops.value = new Array(pokerRunEvent.value.stops.length).fill(false);

  Sentry.setContext("event", {
    eventId: pokerRunEvent.value.id,
    eventName: pokerRunEvent.value.name,
  });
};
initialized();

const expandCollapseAll = (newValue: boolean) => {
  allExpanded.value = newValue;
  expandedStops.value = expandedStops.value.map(() => allExpanded.value);
};

const toggleStop = (index: number) => {
  expandedStops.value[index] = !expandedStops.value[index];
  allExpanded.value = expandedStops.value.every(v => v);
};

// Add new refs
const enteredCode = ref("");

// Add computed properties
const requiresRegistrationCode = computed(() =>
  pokerRunEvent.value?.eventSettings?.isPrivateEvent ?? false
);

const isValidRegistrationCode = computed(() => {
  if (!requiresRegistrationCode.value) return true;
  return enteredCode.value.toLowerCase() ===
    (pokerRunEvent.value?.eventSettings?.registrationCode ?? "").toLowerCase();
});

const isRegisterDisabled = computed(() =>
  requiresRegistrationCode.value && !isValidRegistrationCode.value
);
</script>

<style scoped>
.map-container {
  position: relative;
  height: 200px;
  border-radius: 0.5rem;
  overflow: hidden;
}
</style>
